import React, { useState } from 'react';
import { FaRupeeSign, FaChartLine, FaCalendarAlt, FaInfo, FaShieldAlt, FaQuestion, FaChartPie, FaCalculator, FaChartArea, FaBuilding, FaWallet } from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';

const PostOfficeMISCalculator = () => {
  const [investmentAmount, setInvestmentAmount] = useState('');
  const [investmentPeriod, setInvestmentPeriod] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Current Post Office MIS interest rates (as of 2024)
  const interestRates = {
    1: 7.4,  // 1 year
    2: 7.4,  // 2 years
    3: 7.4,  // 3 years
    5: 7.4   // 5 years
  };

  const calculateMIS = () => {
    if (!investmentAmount || !investmentPeriod) {
      setError('Please fill in all required fields');
      return;
    }

    const P = parseFloat(investmentAmount);
    const n = parseFloat(investmentPeriod);

    if (isNaN(P) || isNaN(n)) {
      setError('Please enter valid numbers');
      return;
    }

    if (n < 1 || n > 5 || !Number.isInteger(n)) {
      setError('Investment period must be 1, 2, 3, or 5 years');
      return;
    }

    const r = interestRates[n] / 100;
    const monthlyInterest = (P * r) / 12;
    const totalInterest = monthlyInterest * 12 * n;
    const totalAmount = P + totalInterest;

    const resultData = {
      monthlyInterest: monthlyInterest.toFixed(2),
      totalInterest: totalInterest.toFixed(2),
      totalAmount: totalAmount.toFixed(2),
      interestRate: r * 100
    };

    setResult(resultData);
    setError('');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const chartOptions = {
    chart: {
      type: 'donut',
      height: 350,
      toolbar: {
        show: false
      }
    },
    colors: ['#10B981', '#3B82F6'],
    labels: ['Principal Amount', 'Interest Earned'],
    legend: {
      position: 'bottom',
      fontSize: '14px',
      fontFamily: 'Inter, sans-serif',
      labels: {
        colors: '#4B5563'
      }
    },
    responsive: [{
      breakpoint: 480,
      options: {
        chart: {
          width: 200
        },
        legend: {
          position: 'bottom'
        }
      }
    }],
    dataLabels: {
      enabled: true,
      style: {
        fontSize: '14px',
        fontFamily: 'Inter, sans-serif',
        colors: ['#4B5563']
      }
    },
    plotOptions: {
      pie: {
        donut: {
          size: '70%'
        }
      }
    }
  };

  const chartSeries = result ? [parseFloat(investmentAmount), parseFloat(result.totalInterest)] : [];

  return (
    <div className="bg-gradient-to-br from-green-50 to-white min-h-screen p-8">
      <Helmet>
        <title>Post Office MIS Calculator</title>
        <meta name="description" content="Calculate your Post Office Monthly Income Scheme returns and monthly interest." />
        <meta property="og:title" content="Post Office MIS Calculator" />
        <meta property="og:description" content="Calculate your Post Office Monthly Income Scheme returns and monthly interest." />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="bg-white rounded-3xl shadow-2xl p-10 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <h2 className="text-4xl font-bold text-gray-800 flex items-center">
            <FaBuilding className="mr-3 text-4xl text-green-600" />
            Post Office MIS Calculator
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
          <div className="bg-gradient-to-r from-green-50 to-white p-6 rounded-2xl shadow-md">
            <label className="block text-green-700 text-sm font-bold mb-3 flex items-center">
              <FaWallet className="inline-block mr-2 text-green-500" />
              Investment Amount (₹)
            </label>
            <div className="relative">
              <FaRupeeSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-green-400" />
              <input
                type="number"
                value={investmentAmount}
                onChange={(e) => setInvestmentAmount(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-green-200 rounded-lg focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all duration-300"
                placeholder="Enter investment amount"
                min="0"
              />
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-50 to-white p-6 rounded-2xl shadow-md">
            <label className="block text-green-700 text-sm font-bold mb-3 flex items-center">
              <FaCalendarAlt className="inline-block mr-2 text-green-500" />
              Investment Period (Years)
            </label>
            <select
              value={investmentPeriod}
              onChange={(e) => setInvestmentPeriod(e.target.value)}
              className="w-full p-3 border border-green-200 rounded-lg focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all duration-300"
            >
              <option value="">Select period</option>
              <option value="1">1 Year (7.4%)</option>
              <option value="2">2 Years (7.4%)</option>
              <option value="3">3 Years (7.4%)</option>
              <option value="5">5 Years (7.4%)</option>
            </select>
          </div>
        </div>

        <button
          onClick={calculateMIS}
          className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-black font-bold py-6 px-12 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 mb-10 border-4 border-green-500"
        >
          <FaCalculator className="inline-block mr-3 text-2xl" />
          Calculate MIS Returns
        </button>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-6 py-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        {result && (
          <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <FaChartPie className="mr-3 text-green-600" />
              Investment Breakdown
            </h2>
            <div className="bg-white p-6 rounded-xl shadow-md">
              <Chart options={chartOptions} series={chartSeries} type="donut" height={350} />
            </div>
            <div className="mt-6 p-6 bg-green-50 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-green-700">Breakdown Details</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Principal Amount</span>
                  <span className="font-semibold text-green-600">
                    {formatCurrency(investmentAmount)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Interest Earned</span>
                  <span className="font-semibold text-blue-600">
                    {formatCurrency(result.totalInterest)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Amount</span>
                  <span className="font-semibold text-green-600">
                    {formatCurrency(result.totalAmount)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {result && (
          <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <FaChartLine className="mr-3 text-green-600" />
              Monthly Income Details
            </h2>
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Monthly Interest</span>
                <span className="font-semibold text-green-600">
                  {formatCurrency(result.monthlyInterest)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Annual Interest Rate</span>
                <span className="font-semibold text-blue-600">
                  {result.interestRate}% p.a.
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Interest Earned</span>
                <span className="font-semibold text-green-600">
                  {formatCurrency(result.totalInterest)}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
          <h2 className="text-2xl font-semibold mb-6 flex items-center">
            <FaInfo className="mr-3 text-green-600" />
            About Post Office MIS
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 bg-green-50 rounded-xl">
              <h3 className="text-xl font-semibold mb-4 text-green-700">Key Features</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Guaranteed returns from Government of India</li>
                <li>Monthly interest payment option</li>
                <li>Minimum investment of ₹1,000</li>
                <li>No TDS on interest</li>
              </ul>
            </div>
            <div className="p-6 bg-green-50 rounded-xl">
              <h3 className="text-xl font-semibold mb-4 text-green-700">Benefits</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Regular monthly income</li>
                <li>Safe and secure investment</li>
                <li>No tax on interest (below ₹50,000)</li>
                <li>Easy to open and maintain</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
          <h2 className="text-2xl font-semibold mb-6 flex items-center">
            <FaQuestion className="mr-3 text-green-600" />
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div className="bg-green-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-green-700">What is Post Office MIS?</h3>
              <p className="text-gray-600">
                Monthly Income Scheme (MIS) is a savings scheme offered by Post Offices that provides a fixed monthly income.
              </p>
            </div>
            <div className="bg-green-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-green-700">What is the minimum investment?</h3>
              <p className="text-gray-600">
                The minimum investment amount is ₹1,000 and can be invested in multiples of ₹1,000.
              </p>
            </div>
            <div className="bg-green-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-green-700">Is MIS tax-free?</h3>
              <p className="text-gray-600">
                Interest earned is exempt from TDS but is taxable under the head 'Income from other sources'.
              </p>
            </div>
            <div className="bg-green-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-green-700">Can I withdraw before maturity?</h3>
              <p className="text-gray-600">
                Premature withdrawal is allowed after 1 year with a penalty of 1% of the principal amount.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-lg">
          <h2 className="text-2xl font-semibold mb-6 flex items-center">
            <FaInfo className="mr-3 text-green-600" />
            How to Use
          </h2>
          <div className="space-y-6">
            <div className="bg-green-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-green-700">Step 1: Enter Investment Amount</h3>
              <p className="text-gray-600">
                Input the amount you wish to invest in Post Office MIS.
              </p>
            </div>
            <div className="bg-green-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-green-700">Step 2: Select Investment Period</h3>
              <p className="text-gray-600">
                Choose your preferred investment period (1, 2, 3, or 5 years).
              </p>
            </div>
            <div className="bg-green-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-green-700">Step 3: Calculate Returns</h3>
              <p className="text-gray-600">
                Click the "Calculate MIS Returns" button to see your monthly income and total returns.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostOfficeMISCalculator;
